import { useState, useCallback } from "react";
import { ref, push, get, query, orderByChild, limitToLast, startAt } from "firebase/database";
import { database } from "src/initialiseFirebase";
import { useAuth } from "src/contexts/AuthContext";
import type { GameHistoryEntry, PlayerId, GameType, Side, ExpertLevel, GameMode, WeeklyStats } from "src/core/types";

type UseGameHistoryReturn = {
  addGameToHistory: (game: {
    gameId: string;
    gameType: GameType;
    mode: GameMode;
    result: "won" | "lost" | "abandoned";
    opponent: PlayerId | "AI";
    numberOfTurns: number;
    startingSide: Side;
    expertLevel: ExpertLevel;
    duration: number;
  }) => Promise<void>;
  getRecentGames: (limit: number) => Promise<GameHistoryEntry[]>;
  getWeeklyStats: () => Promise<WeeklyStats>;
  loading: boolean;
  error: string | null;
};

/**
 * Hook for managing game history
 * Tracks completed games for both single-player and multiplayer modes
 */
export const useGameHistory = (): UseGameHistoryReturn => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const addGameToHistory = useCallback(
    async (game: {
      gameId: string;
      gameType: GameType;
      mode: GameMode;
      result: "won" | "lost" | "abandoned";
      opponent: PlayerId | "AI";
      numberOfTurns: number;
      startingSide: Side;
      expertLevel: ExpertLevel;
      duration: number;
    }): Promise<void> => {
      if (!user) {
        console.warn("Cannot add game to history: user not authenticated");
        return;
      }

      try {
        setLoading(true);
        const historyRef = ref(database, `users/${user.uid}/gameHistory`);

        const entry: GameHistoryEntry = {
          ...game,
          completedAt: Date.now()
        };

        await push(historyRef, entry);
        setError(null);
      } catch (err) {
        console.error("Error adding game to history:", err);
        setError("Failed to save game to history");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const getRecentGames = useCallback(
    async (limit: number): Promise<GameHistoryEntry[]> => {
      if (!user) return [];

      try {
        setLoading(true);
        const historyRef = ref(database, `users/${user.uid}/gameHistory`);
        const recentQuery = query(historyRef, orderByChild("completedAt"), limitToLast(limit));

        const snapshot = await get(recentQuery);

        if (!snapshot.exists()) {
          return [];
        }

        const games: GameHistoryEntry[] = [];
        snapshot.forEach((child) => {
          const data = child.val();
          games.push({
            gameId: data.gameId,
            gameType: data.gameType,
            mode: data.mode,
            result: data.result,
            opponent: data.opponent,
            numberOfTurns: data.numberOfTurns,
            startingSide: data.startingSide,
            expertLevel: data.expertLevel,
            completedAt: data.completedAt,
            duration: data.duration
          });
        });

        // Sort by completedAt descending
        games.sort((a, b) => b.completedAt - a.completedAt);

        setError(null);
        return games;
      } catch (err) {
        console.error("Error fetching recent games:", err);
        setError("Failed to load recent games");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const getWeeklyStats = useCallback(async (): Promise<WeeklyStats> => {
    if (!user) {
      return {
        gamesPlayed: 0,
        gamesWon: 0,
        gamesLost: 0,
        totalTurns: 0,
        averageTurns: 0,
        winRate: 0
      };
    }

    try {
      setLoading(true);
      const historyRef = ref(database, `users/${user.uid}/gameHistory`);

      // Use server-side filtering with orderByChild and startAt
      // This requires a database index on completedAt (defined in database.rules.json)
      const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const weeklyQuery = query(historyRef, orderByChild("completedAt"), startAt(oneWeekAgo));

      const snapshot = await get(weeklyQuery);

      if (!snapshot.exists()) {
        return {
          gamesPlayed: 0,
          gamesWon: 0,
          gamesLost: 0,
          totalTurns: 0,
          averageTurns: 0,
          winRate: 0
        };
      }

      const recentGames: GameHistoryEntry[] = [];

      snapshot.forEach((child) => {
        const data = child.val();
        recentGames.push({
          gameId: data.gameId,
          gameType: data.gameType,
          mode: data.mode,
          result: data.result,
          opponent: data.opponent,
          numberOfTurns: data.numberOfTurns,
          startingSide: data.startingSide,
          expertLevel: data.expertLevel,
          completedAt: data.completedAt,
          duration: data.duration
        });
      });

      const gamesPlayed = recentGames.length;
      const gamesWon = recentGames.filter((g) => g.result === "won").length;
      const gamesLost = recentGames.filter((g) => g.result === "lost").length;
      const totalTurns = recentGames.reduce((sum, g) => sum + g.numberOfTurns, 0);
      const averageTurns = gamesPlayed > 0 ? totalTurns / gamesPlayed : 0;
      const winRate = gamesPlayed > 0 ? gamesWon / gamesPlayed : 0;

      setError(null);
      return {
        gamesPlayed,
        gamesWon,
        gamesLost,
        totalTurns,
        averageTurns: Math.round(averageTurns),
        winRate: Math.round(winRate * 100) / 100
      };
    } catch (err) {
      console.error("Error fetching weekly stats:", err);
      setError("Failed to load statistics");
      return {
        gamesPlayed: 0,
        gamesWon: 0,
        gamesLost: 0,
        totalTurns: 0,
        averageTurns: 0,
        winRate: 0
      };
    } finally {
      setLoading(false);
    }
  }, [user]);

  return {
    addGameToHistory,
    getRecentGames,
    getWeeklyStats,
    loading,
    error
  };
};
